import { LANGUAGE_OPTIONS } from "@/i18n/i18n";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type LanguageSwitchProps = {
  className?: string;
};

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={`h-3.5 w-3.5 shrink-0 text-ink-tertiary transition-transform duration-200 ${
        open ? "rotate-180" : ""
      }`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function CheckIcon({ className = "h-3.5 w-3.5 shrink-0 text-accent" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M20 6 9 17l-5-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function LanguageSwitch({ className = "" }: LanguageSwitchProps) {
  const { t, i18n } = useTranslation();
  const listId = useId();
  const btnId = useId();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const resolved = i18n.resolvedLanguage ?? i18n.language;
  const value = LANGUAGE_OPTIONS.some(o => o.code === resolved) ? resolved : "pt-BR";
  const current = LANGUAGE_OPTIONS.find(o => o.code === value) ?? LANGUAGE_OPTIONS[0];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const el = rootRef.current;
      if (el && e.target instanceof Node && !el.contains(e.target)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={`relative shrink-0 ${className}`.trim()}>
      <button
        id={btnId}
        type="button"
        aria-label={t("nav.selectLanguage")}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen(v => !v)}
        className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border-soft/70 bg-surface-1/50 px-2.5 font-mono text-[0.62rem] uppercase tracking-[0.12em] text-ink-secondary shadow-[inset_0_1px_0_rgb(255_255_255/0.05)] outline-offset-4 transition-[color,background-color,border-color,box-shadow] hover:border-accent/35 hover:bg-surface-1/75 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/55 max-[380px]:px-2 max-[380px]:text-[0.55rem]"
      >
        <span className="min-w-[2.75rem] text-left tabular-nums">{current.label}</span>
        <ChevronIcon open={open} />
      </button>

      {open ? (
        <div
          id={listId}
          role="listbox"
          aria-labelledby={btnId}
          className="absolute right-0 top-[calc(100%+8px)] z-[200] min-w-[10.5rem] origin-top overflow-hidden rounded-xl border border-border-strong/40 bg-canvas/92 py-1 shadow-[0_16px_48px_-12px_rgb(0_0_0/0.72),0_0_0_1px_rgb(201_167_232/0.06)] backdrop-blur-md"
        >
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/[0.04] to-transparent"
            aria-hidden
          />
          <div className="relative py-0.5">
            {LANGUAGE_OPTIONS.map(opt => {
              const selected = opt.code === value;
              return (
                <button
                  key={opt.code}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left font-mono text-[0.65rem] uppercase tracking-[0.1em] transition-colors ${
                    selected
                      ? "bg-surface-2/80 text-ink"
                      : "text-ink-secondary hover:bg-surface-2/50 hover:text-ink"
                  }`}
                  onClick={() => {
                    void i18n.changeLanguage(opt.code);
                    setOpen(false);
                  }}
                >
                  <span className="tabular-nums">{opt.label}</span>
                  {selected ? (
                    <CheckIcon />
                  ) : (
                    <span className="inline-flex h-3.5 w-3.5 shrink-0" aria-hidden />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
